import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/api';
import { getGlobalUserRoleBadgeInfo } from '../utils/roleBadges';
import './Users.css';

const Users = () => {
  // Normaliza papéis legados para os novos (QA/DEV/GERENCIAL/ADM)
  const normalizeRoleToNew = (role) => {
    const r = String(role || '').toLowerCase();
    if (r === 'admin' || r === 'adm') return 'ADM';
    if (r === 'manager' || r === 'gerencial') return 'GERENCIAL';
    if (r === 'tester' || r === 'qa') return 'QA';
    if (r === 'viewer' || r === 'consulta' || r === 'dev') return 'DEV';
    return role ? String(role).toUpperCase() : 'QA';
  };
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'QA',
    is_active: true
  });

  const { showSuccess, showError } = useNotification();
  const { hasPermission, user } = useAuth();
  const roleUpper = String(user?.role || '').toUpperCase();
  const isTrialOrUnitario = roleUpper === 'TRIAL' || roleUpper === 'UNITARIO';

  // Gating por permissões efetivas com fallback para roles legadas
  const canCreateUser = hasPermission('canCreateUser', ['ADM', 'ADMIN', 'GERENCIAL']) && !isTrialOrUnitario;
  const canEditUser = hasPermission('canEditUser', ['ADM', 'ADMIN', 'GERENCIAL']);
  const canDeleteUser = hasPermission('canDeleteUser', ['ADM', 'ADMIN']);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (roleFilter) {
        // Mapear valores de filtro para papéis legados no backend
        const roleMap = {
          admin: 'ADM',
          manager: 'GERENCIAL',
          tester: 'QA'
        };
        const mappedRole = roleMap[roleFilter] || roleFilter;
        params.append('role', mappedRole);
      }

      const response = await fetch(getApiUrl(`users?${params}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.data);
        setTotalPages(data.pagination.pages);
      } else {
        showError('Erro ao carregar usuários');
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      showError('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, roleFilter, showError]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateUser = async () => {
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.password) {
      showError('Preencha todos os campos obrigatórios');
      return;
    }

    const nameRegex = /^[A-Za-zÀ-ÿ\s]+$/;
    if (!nameRegex.test(String(formData.first_name).trim()) || !nameRegex.test(String(formData.last_name).trim())) {
      showError('Nome e sobrenome devem conter apenas letras e espaços');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const mappedRole = normalizeRoleToNew(formData.role);
      const payload = { ...formData, role: mappedRole };
      const response = await fetch(getApiUrl('users'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        showSuccess('Usuário criado com sucesso');
        setShowCreateModal(false);
        resetForm();
        fetchUsers();
      } else {
        const error = await response.json();
        showError(error.message || 'Erro ao criar usuário');
      }
    } catch (error) {
      console.error('Erro ao criar usuário:', error);
      showError('Erro ao criar usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async () => {
    if (!formData.email) {
      showError('Email é obrigatório');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      // Enviar apenas campos aceitos pelo backend
      const mappedRole = normalizeRoleToNew(formData.role);
      const updateData = {
        email: formData.email,
        role: mappedRole,
        is_active: formData.is_active
      };
      // Incluir senha somente se informada e válida
      if (formData.password && formData.password.trim().length >= 6) {
        updateData.password = formData.password.trim();
      }

      const response = await fetch(getApiUrl(`users/${selectedUser.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        showSuccess('Usuário atualizado com sucesso');
        setShowEditModal(false);
        resetForm();
        fetchUsers();
      } else {
        const error = await response.json();
        showError(error.message || 'Erro ao atualizar usuário');
      }
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      showError('Erro ao atualizar usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm('Tem certeza que deseja desativar este usuário?')) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`users/${userId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showSuccess('Usuário desativado com sucesso');
        fetchUsers();
      } else {
        const error = await response.json();
        showError(error.message || 'Erro ao desativar usuário');
      }
    } catch (error) {
      console.error('Erro ao desativar usuário:', error);
      showError('Erro ao desativar usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`users/${userId}/activate`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showSuccess('Usuário reativado com sucesso');
        fetchUsers();
      } else {
        const error = await response.json();
        showError(error.message || 'Erro ao reativar usuário');
      }
    } catch (error) {
      console.error('Erro ao reativar usuário:', error);
      showError('Erro ao reativar usuário');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      password: '',
      role: normalizeRoleToNew(user.role),
      is_active: user.is_active
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      role: 'QA',
      is_active: true
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const renderRoleBadge = (role) => {
    const info = getGlobalUserRoleBadgeInfo(role);
    return <span className={`role-badge ${info.class}`}>{info.text}</span>;
  };

  const getStatusBadge = (isActive) => {
    return (
      <span className={`status-badge ${isActive ? 'status-active' : 'status-inactive'}`}>
        {isActive ? 'Ativo' : 'Inativo'}
      </span>
    );
  };

  return (
    <div className="users-container">
      <div className="users-header">
        <div className="header-left">
          <button 
            className="btn-back"
            onClick={() => navigate('/overview')}
            title="Voltar ao Menu Principal"
          >
            ← Voltar ao Menu
          </button>
          <h1>Gestão de Usuários</h1>
        </div>
        {canCreateUser && (
          <button 
            className="btn-primary"
            onClick={() => setShowCreateModal(true)}
            disabled={loading}
          >
            Novo Usuário
          </button>
        )}
      </div>

      <div className="users-filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Buscar por nome ou email..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="">Todos os papéis</option>
            <option value="QA">QA</option>
            <option value="DEV">DEV</option>
            <option value="GERENCIAL">GERENCIAL</option>
            <option value="ADM">ADM</option>
          </select>
        </div>
      </div>

      {loading && <div className="loading">Carregando...</div>}

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Nome de Usuário</th>
              <th>Email</th>
              <th>Papel</th>
              <th>Status</th>
              <th>Criado em</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td>{`${user.first_name} ${user.last_name}`}</td>
                <td>{user.email}</td>
                <td>{renderRoleBadge(user.role)}</td>
                <td>{getStatusBadge(user.is_active)}</td>
                <td>{new Date(user.created_at).toLocaleDateString('pt-BR')}</td>
                <td>
                  <div className="action-buttons">
                    {canEditUser && (
                      <button 
                        className="btn-secondary btn-sm"
                        onClick={() => openEditModal(user)}
                        disabled={loading}
                      >
                        Editar
                      </button>
                    )}
                    {user.is_active ? (
                      canDeleteUser && (
                        <button 
                          className="btn-danger btn-sm"
                          onClick={() => handleDeactivateUser(user.id)}
                          disabled={loading}
                        >
                          Desativar
                        </button>
                      )
                    ) : (
                      canEditUser && (
                        <button 
                          className="btn-success btn-sm"
                          onClick={() => handleActivateUser(user.id)}
                          disabled={loading}
                        >
                          Ativar
                        </button>
                      )
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && !loading && (
          <div className="empty-state">
            <p>Nenhum usuário encontrado</p>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button 
            className="btn-secondary"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1 || loading}
          >
            Anterior
          </button>
          <span className="page-info">
            Página {currentPage} de {totalPages}
          </span>
          <button 
            className="btn-secondary"
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages || loading}
          >
            Próxima
          </button>
        </div>
      )}

      {/* Modal de Criação */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Novo Usuário</h2>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Primeiro Nome *</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    const allowed = ['Tab','Escape','Backspace','Delete','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End','Enter','Control','Shift','Alt','Meta','CapsLock','Dead']
                    if (allowed.includes(e.key)) return;
                    const el = e.target;
                    const cur = el.value || '';
                    const selStart = el.selectionStart ?? cur.length;
                    const selEnd = el.selectionEnd ?? cur.length;
                    const willLen = cur.length - (selEnd - selStart) + 1;
                    if (e.key === ' ') { if (willLen > 100) e.preventDefault(); return; }
                    if (e.key.length === 1 && /^[\p{L}]$/u.test(e.key)) { if (willLen > 100) e.preventDefault(); return; }
                    e.preventDefault();
                  }}
                  onBeforeInput={(e) => {
                    if ((e.inputType && e.inputType.startsWith('delete')) || e.data == null) return;
                    if (/^[\p{L}\s]+$/u.test(e.data)) {
                      const el = e.target;
                      const cur = el.value || '';
                      const selStart = el.selectionStart ?? cur.length;
                      const selEnd = el.selectionEnd ?? cur.length;
                      const willLen = cur.length - (selEnd - selStart) + e.data.length;
                      if (willLen > 100) e.preventDefault();
                      return;
                    }
                    e.preventDefault();
                  }}
                  onPaste={(e) => {
                    const text = e.clipboardData.getData('text');
                    if (!/^[\p{L}\s]+$/u.test(text)) { e.preventDefault(); return; }
                    const el = e.target;
                    const cur = el.value || '';
                    const selStart = el.selectionStart ?? cur.length;
                    const selEnd = el.selectionEnd ?? cur.length;
                    const willLen = cur.length - (selEnd - selStart) + text.length;
                    if (willLen > 100) e.preventDefault();
                  }}
                  onDrop={(e) => e.preventDefault()}
                  pattern="^[A-Za-zÀ-ÿ\\s]+$"
                  title="Use apenas letras (A–Z, á–ú) e espaços"
                  placeholder="Digite o primeiro nome"
                />
              </div>
              
              <div className="form-group">
                <label>Último Nome *</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    const allowed = ['Tab','Escape','Backspace','Delete','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End','Enter','Control','Shift','Alt','Meta','CapsLock','Dead']
                    if (allowed.includes(e.key)) return;
                    const el = e.target;
                    const cur = el.value || '';
                    const selStart = el.selectionStart ?? cur.length;
                    const selEnd = el.selectionEnd ?? cur.length;
                    const willLen = cur.length - (selEnd - selStart) + 1;
                    if (e.key === ' ') { if (willLen > 100) e.preventDefault(); return; }
                    if (e.key.length === 1 && /^[\p{L}]$/u.test(e.key)) { if (willLen > 100) e.preventDefault(); return; }
                    e.preventDefault();
                  }}
                  onBeforeInput={(e) => {
                    if ((e.inputType && e.inputType.startsWith('delete')) || e.data == null) return;
                    if (/^[\p{L}\s]+$/u.test(e.data)) {
                      const el = e.target;
                      const cur = el.value || '';
                      const selStart = el.selectionStart ?? cur.length;
                      const selEnd = el.selectionEnd ?? cur.length;
                      const willLen = cur.length - (selEnd - selStart) + e.data.length;
                      if (willLen > 100) e.preventDefault();
                      return;
                    }
                    e.preventDefault();
                  }}
                  onPaste={(e) => {
                    const text = e.clipboardData.getData('text');
                    if (!/^[\p{L}\s]+$/u.test(text)) { e.preventDefault(); return; }
                    const el = e.target;
                    const cur = el.value || '';
                    const selStart = el.selectionStart ?? cur.length;
                    const selEnd = el.selectionEnd ?? cur.length;
                    const willLen = cur.length - (selEnd - selStart) + text.length;
                    if (willLen > 100) e.preventDefault();
                  }}
                  onDrop={(e) => e.preventDefault()}
                  pattern="^[A-Za-zÀ-ÿ\\s]+$"
                  title="Use apenas letras (A–Z, á–ú) e espaços"
                  placeholder="Digite o último nome"
                />
              </div>
              
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Digite o email"
                />
              </div>

              <div className="form-group">
                <label>Senha *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Digite a senha (mín. 6 caracteres)"
                />
              </div>

              <div className="form-group">
                <label>Papel *</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                >
                  <option value="QA">QA</option>
                  <option value="DEV">DEV</option>
                  <option value="GERENCIAL">GERENCIAL</option>
                  <option value="ADM">ADM</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
              >
                Cancelar
              </button>
              <button 
                className="btn-primary"
                onClick={handleCreateUser}
                disabled={loading}
              >
                Criar Usuário
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Editar Usuário</h2>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Primeiro Nome *</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    const allowed = ['Tab','Escape','Backspace','Delete','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End','Enter','Control','Shift','Alt','Meta','CapsLock','Dead']
                    if (allowed.includes(e.key)) return;
                    const el = e.target;
                    const cur = el.value || '';
                    const selStart = el.selectionStart ?? cur.length;
                    const selEnd = el.selectionEnd ?? cur.length;
                    const willLen = cur.length - (selEnd - selStart) + 1;
                    if (e.key === ' ') { if (willLen > 100) e.preventDefault(); return; }
                    if (e.key.length === 1 && /^[\p{L}]$/u.test(e.key)) { if (willLen > 100) e.preventDefault(); return; }
                    e.preventDefault();
                  }}
                  onBeforeInput={(e) => {
                    if ((e.inputType && e.inputType.startsWith('delete')) || e.data == null) return;
                    if (/^[\p{L}\s]+$/u.test(e.data)) {
                      const el = e.target;
                      const cur = el.value || '';
                      const selStart = el.selectionStart ?? cur.length;
                      const selEnd = el.selectionEnd ?? cur.length;
                      const willLen = cur.length - (selEnd - selStart) + e.data.length;
                      if (willLen > 100) e.preventDefault();
                      return;
                    }
                    e.preventDefault();
                  }}
                  onPaste={(e) => {
                    const text = e.clipboardData.getData('text');
                    if (!/^[\p{L}\s]+$/u.test(text)) { e.preventDefault(); return; }
                    const el = e.target;
                    const cur = el.value || '';
                    const selStart = el.selectionStart ?? cur.length;
                    const selEnd = el.selectionEnd ?? cur.length;
                    const willLen = cur.length - (selEnd - selStart) + text.length;
                    if (willLen > 100) e.preventDefault();
                  }}
                  onDrop={(e) => e.preventDefault()}
                  pattern="^[A-Za-zÀ-ÿ\\s]+$"
                  title="Use apenas letras (A–Z, á–ú) e espaços"
                  placeholder="Digite o primeiro nome"
                />
              </div>
              
              <div className="form-group">
                <label>Último Nome *</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    const allowed = ['Tab','Escape','Backspace','Delete','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End','Enter','Control','Shift','Alt','Meta','CapsLock','Dead']
                    if (allowed.includes(e.key)) return;
                    const el = e.target;
                    const cur = el.value || '';
                    const selStart = el.selectionStart ?? cur.length;
                    const selEnd = el.selectionEnd ?? cur.length;
                    const willLen = cur.length - (selEnd - selStart) + 1;
                    if (e.key === ' ') { if (willLen > 100) e.preventDefault(); return; }
                    if (e.key.length === 1 && /^[\p{L}]$/u.test(e.key)) { if (willLen > 100) e.preventDefault(); return; }
                    e.preventDefault();
                  }}
                  onBeforeInput={(e) => {
                    if ((e.inputType && e.inputType.startsWith('delete')) || e.data == null) return;
                    if (/^[\p{L}\s]+$/u.test(e.data)) {
                      const el = e.target;
                      const cur = el.value || '';
                      const selStart = el.selectionStart ?? cur.length;
                      const selEnd = el.selectionEnd ?? cur.length;
                      const willLen = cur.length - (selEnd - selStart) + e.data.length;
                      if (willLen > 100) e.preventDefault();
                      return;
                    }
                    e.preventDefault();
                  }}
                  onPaste={(e) => {
                    const text = e.clipboardData.getData('text');
                    if (!/^[\p{L}\s]+$/u.test(text)) { e.preventDefault(); return; }
                    const el = e.target;
                    const cur = el.value || '';
                    const selStart = el.selectionStart ?? cur.length;
                    const selEnd = el.selectionEnd ?? cur.length;
                    const willLen = cur.length - (selEnd - selStart) + text.length;
                    if (willLen > 100) e.preventDefault();
                  }}
                  onDrop={(e) => e.preventDefault()}
                  pattern="^[A-Za-zÀ-ÿ\\s]+$"
                  title="Use apenas letras (A–Z, á–ú) e espaços"
                  placeholder="Digite o último nome"
                />
              </div>
              
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Digite o email"
                />
              </div>

              <div className="form-group">
                <label>Nova Senha (deixe em branco para não alterar)</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Digite a nova senha (opcional)"
                />
              </div>

              <div className="form-group">
                <label>Papel *</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                >
                  <option value="QA">QA</option>
                  <option value="DEV">DEV</option>
                  <option value="GERENCIAL">GERENCIAL</option>
                  <option value="ADM">ADM</option>
                </select>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                  />
                  Usuário ativo
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
              >
                Cancelar
              </button>
              <button 
                className="btn-primary"
                onClick={handleEditUser}
                disabled={loading}
              >
                Salvar Alterações
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
